import { useState, useEffect, useRef, type Dispatch, type SetStateAction } from 'react'
import { searchMailAudienceUsers, type AudienceUserMatch } from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

interface SpecificUsersPickerProps {
  selected: AudienceUserMatch[]
  onChange: Dispatch<SetStateAction<AudienceUserMatch[]>>
}

const inputStyles = `w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-4 py-2.5 text-base transition-all duration-200 focus:border-[#503864] dark:focus:border-[#7f4f9a] focus:outline-none focus:ring-2 focus:ring-[#503864]/20 dark:focus:ring-[#7f4f9a]/20`

function ReachBadge({ user }: { user: AudienceUserMatch }) {
  if (user.hard_bounced) {
    return (
      <span className="shrink-0 rounded-full bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-xs text-red-700 dark:text-red-300">
        Bounced
      </span>
    )
  }
  if (!user.broadcast_consent) {
    return (
      <span className="shrink-0 rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-xs text-amber-800 dark:text-amber-300">
        Opted out
      </span>
    )
  }
  return null
}

function UserLabel({ user }: { user: AudienceUserMatch }) {
  return (
    <div className="min-w-0">
      <p className="truncate text-sm text-gray-900 dark:text-gray-100">
        {user.name ? `${user.name} · ` : ''}
        {user.email}
      </p>
      {user.shop_name && (
        <p className="truncate text-xs text-gray-500 dark:text-gray-400">Shop: {user.shop_name}</p>
      )}
    </div>
  )
}

/**
 * Pick individual recipients — typed as emails / shop ids / user ids, or chosen
 * from a search modal. Meant for test sends to a handful of real accounts.
 */
export function SpecificUsersPicker({ selected, onChange }: SpecificUsersPickerProps) {
  const [input, setInput] = useState('')
  const [adding, setAdding] = useState(false)
  const [inputError, setInputError] = useState<string | null>(null)
  const [browseQuery, setBrowseQuery] = useState<string | null>(null)

  const selectedIds = new Set(selected.map((u) => u.user_id))

  // Functional updates so a slow Add lookup can't clobber picks made meanwhile.
  const addUsers = (users: AudienceUserMatch[]) => {
    onChange((prev) => {
      const next = [...prev]
      const seen = new Set(prev.map((u) => u.user_id))
      for (const user of users) {
        if (!seen.has(user.user_id)) {
          seen.add(user.user_id)
          next.push(user)
        }
      }
      return next
    })
  }

  const toggleUser = (user: AudienceUserMatch) => {
    onChange((prev) =>
      prev.some((u) => u.user_id === user.user_id)
        ? prev.filter((u) => u.user_id !== user.user_id)
        : [...prev, user]
    )
  }

  // Each token must resolve to exactly the user it names; free text with
  // several matches opens the browse modal instead of guessing.
  const handleAdd = async () => {
    const tokens = input.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean)
    if (tokens.length === 0) return
    setAdding(true)
    setInputError(null)
    const found: AudienceUserMatch[] = []
    const missing: string[] = []
    const ambiguous: string[] = []
    try {
      for (const token of tokens) {
        const res = await searchMailAudienceUsers(token)
        const users = res.ok && res.data ? res.data.users : []
        const t = token.toLowerCase()
        let hits: AudienceUserMatch[]
        if (token.includes('@')) {
          hits = users.filter((u) => u.email.toLowerCase() === t)
        } else if (UUID_RE.test(token)) {
          hits = users.filter((u) => u.user_id === t || u.shop_id === t)
        } else if (users.length > 1) {
          ambiguous.push(token)
          continue
        } else {
          hits = users
        }
        if (hits.length > 0) found.push(...hits)
        else missing.push(token)
      }
    } catch {
      setInputError('Lookup failed — try again.')
      setAdding(false)
      return
    }
    addUsers(found)
    setAdding(false)
    setInput([...missing, ...ambiguous.slice(1)].join(', '))
    setInputError(missing.length > 0 ? `No user found for ${missing.join(', ')}` : null)
    if (ambiguous.length > 0) setBrowseQuery(ambiguous[0])
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleAdd()
            }
          }}
          placeholder="Email, shop ID or user ID"
          className={inputStyles}
        />
        <CustomButton onClick={handleAdd} disabled={adding || !input.trim()} variant="secondary">
          {adding ? 'Adding...' : 'Add'}
        </CustomButton>
        <CustomButton onClick={() => setBrowseQuery('')} variant="secondary">
          Browse
        </CustomButton>
      </div>
      <p className="text-xs text-gray-500">Separate several with commas. Browse searches by name or shop too.</p>
      {inputError && <p className="text-sm text-red-600 dark:text-red-400">{inputError}</p>}

      {selected.length > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
          {selected.map((user) => (
            <div key={user.user_id} className="flex items-center gap-3 px-3 py-2">
              <div className="flex-1 min-w-0">
                <UserLabel user={user} />
              </div>
              <ReachBadge user={user} />
              <button
                type="button"
                onClick={() => toggleUser(user)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label={`Remove ${user.email}`}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {selected.some((u) => u.hard_bounced || !u.broadcast_consent) && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Opted-out and bounced users are skipped when the campaign sends.
        </p>
      )}

      {browseQuery !== null && (
        <BrowseUsersModal
          initialQuery={browseQuery}
          selectedIds={selectedIds}
          onToggle={toggleUser}
          onClose={() => setBrowseQuery(null)}
        />
      )}
    </div>
  )
}

interface BrowseUsersModalProps {
  initialQuery: string
  selectedIds: Set<string>
  onToggle: (user: AudienceUserMatch) => void
  onClose: () => void
}

function BrowseUsersModal({ initialQuery, selectedIds, onToggle, onClose }: BrowseUsersModalProps) {
  const [query, setQuery] = useState(initialQuery)
  const [results, setResults] = useState<AudienceUserMatch[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const requestSeq = useRef(0)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Debounced search; a sequence number drops responses that arrive out of order.
  useEffect(() => {
    const q = query.trim()
    if (!q) {
      setResults([])
      setError(null)
      return
    }
    const seq = ++requestSeq.current
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await searchMailAudienceUsers(q)
        if (seq !== requestSeq.current) return
        if (res.ok && res.data) {
          setResults(res.data.users)
          setError(null)
        } else {
          setResults([])
          setError(res.error?.message || 'Search failed')
        }
      } catch {
        if (seq === requestSeq.current) setError('Search failed')
      } finally {
        if (seq === requestSeq.current) setLoading(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [query])

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-2xl bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <h3 className="font-heading text-lg font-bold text-gray-900 dark:text-gray-100">Choose recipients</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <input
          type="text"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search email, name, shop name or ID"
          className={inputStyles}
        />

        <div className="mt-3 min-h-[8rem] flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#503864] border-t-transparent" />
            </div>
          ) : error ? (
            <p className="py-4 text-sm text-red-600 dark:text-red-400">{error}</p>
          ) : !query.trim() ? (
            <p className="py-4 text-sm text-gray-500 dark:text-gray-400">Start typing to find users.</p>
          ) : results.length === 0 ? (
            <p className="py-4 text-sm text-gray-500 dark:text-gray-400">No users match.</p>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {results.map((user) => (
                <li key={user.user_id}>
                  <label className="flex cursor-pointer items-center gap-3 px-1 py-2 hover:bg-gray-50 dark:hover:bg-white/5">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(user.user_id)}
                      onChange={() => onToggle(user)}
                      className="h-4 w-4 accent-[#503864]"
                    />
                    <div className="flex-1 min-w-0">
                      <UserLabel user={user} />
                    </div>
                    <ReachBadge user={user} />
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-gray-500 dark:text-gray-400">{selectedIds.size} selected</span>
          <CustomButton onClick={onClose} variant="primary">
            Done
          </CustomButton>
        </div>
      </div>
    </div>
  )
}
